// src/api/games.api.js - NEW FILE FOR GAME-SPECIFIC API CALLS
import apiClient from './apiClient';

/**
 * Fetches available challenges/games from backend
 */
export const getAvailableChallenges = async (params = {}) => {
  try {
    const response = await apiClient.get('/challenges/available', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch challenges:', error);
    throw error;
  }
};

/**
 * Fetches a specific challenge by ID
 */
export const getChallengeById = async (challengeId) => {
  try {
    const response = await apiClient.get(`/challenges/${challengeId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch challenge ${challengeId}:`, error);
    throw error;
  }
};

/**
 * Submits game/simulation results to backend
 */
export const submitGameResults = async (gameData) => {
  try {
    const {
      challengeId,
      gameType,
      score,
      timeSpent,
      decisions = {},
      correct = 0,
      incorrect = 0,
      total = 0,
      earnedXp = 0,
      completionData = {}
    } = gameData;

    // Clean completionData to remove any arena_id that might be a string
    const cleanedCompletionData = { ...completionData };
    if (cleanedCompletionData.arena_id && typeof cleanedCompletionData.arena_id === 'string') {
      // Remove arena_id if it's not a valid UUID (category strings like "malware_ransomware")
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(cleanedCompletionData.arena_id)) {
        delete cleanedCompletionData.arena_id;
      }
    }

    // Clean decisions to remove any arena_id that might be a string
    const cleanedDecisions = { ...decisions };
    if (cleanedDecisions.arena_id && typeof cleanedDecisions.arena_id === 'string') {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(cleanedDecisions.arena_id)) {
        delete cleanedDecisions.arena_id;
      }
    }

    const payload = {
      game_type: gameType,
      score: score,
      time_taken: timeSpent,
      passed: score >= 70,
      decisions: cleanedDecisions,
      performance_metrics: {
        correct_decisions: correct,
        incorrect_decisions: incorrect,
        total_scenarios: total,
        accuracy_percentage: total > 0 ? Math.round((correct / total) * 100) : 0
      },
      completion_data: cleanedCompletionData,
      xp_earned: earnedXp
      // Explicitly DO NOT include arena_id here - let backend use challenge.category as fallback
    };

    console.log('🎮 Submitting game results:', { challengeId, payload });

    const response = await apiClient.post(`/challenges/${challengeId}/submit-results`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to submit game results:', error);
    throw error;
  }
};

/**
 * Submits challenge attempt (for quiz-style challenges)
 */
export const submitChallengeAttempt = async (challengeId, attemptData) => {
  try {
    const response = await apiClient.post(`/challenges/${challengeId}/attempt`, attemptData);
    return response.data;
  } catch (error) {
    console.error(`Failed to submit challenge attempt for ${challengeId}:`, error);
    throw error;
  }
};

/**
 * Gets user's challenge history
 */
export const getUserChallengeHistory = async (params = {}) => {
  try {
    const response = await apiClient.get('/challenges/user/history', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user challenge history:', error);
    throw error;
  }
};

/**
 * Gets user's game progress and statistics
 */
export const getUserGameProgress = async () => {
  try {
    const response = await apiClient.get('/games/progress');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user game progress:', error);
    throw error;
  }
};

/**
 * Gets AI-generated scenarios for a specific topic
 */
export const getAIScenarios = async (topic, userProfile) => {
  try {
    const response = await apiClient.post('/ai/scenarios/curate', {
      topic: topic,
      user_profile: userProfile
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to get AI scenarios for topic ${topic}:`, error);
    throw error;
  }
};

/**
 * Submits scenario interaction data for AI learning
 */
export const submitScenarioInteraction = async (scenarioId, interactionData) => {
  try {
    const response = await apiClient.post(`/ai/scenarios/${scenarioId}/interaction`, interactionData);
    return response.data;
  } catch (error) {
    console.error('Failed to submit scenario interaction:', error);
    throw error;
  }
};